import { useAddress, useContract } from '@thirdweb-dev/react';
import { NATIVE_TOKEN_ADDRESS } from '@thirdweb-dev/sdk';
import { add } from 'date-fns';
import { useAsyncFn } from 'react-use';

import routerAbi from 'abis/router.json';

import { numberToBigNumberFixed } from '@app/helpers/format';
import { getSwapSettings } from '@app/hooks/swap/useSwapSettings';
import { Token } from '@app/types/token';
import { useConfig } from '@app/config';

type TokenItem = {
  token: Token | null;
  amount: string | null;
};

export function useAddLiquidity(from: TokenItem, to: TokenItem) {
  const address = useAddress();
  const config = useConfig();
  const { contract } = useContract(config?.CONTRACTS.ROUTER, routerAbi);

  const [{ loading, error }, addLiquidity] = useAsyncFn(async () => {
    if (
      !from.amount ||
      !to.amount ||
      !contract ||
      !address ||
      !from.token ||
      !to.token
    ) {
      return undefined;
    }

    const settings = await getSwapSettings();

    if (from.token.contractAddress === NATIVE_TOKEN_ADDRESS) {
      // eth => erc20
      return contract.call(
        'addLiquidityETH',
        [
          to.token.contractAddress,
          numberToBigNumberFixed(+to.amount, to.token.decimals),
          0,
          0,
          address,
          add(new Date(), { minutes: settings.deadline }).getTime()
        ],
        {
          value: numberToBigNumberFixed(+from.amount, from.token.decimals)
        }
      );
    } else if (to.token.contractAddress === NATIVE_TOKEN_ADDRESS) {
      // erc20 => eth
      return contract.call(
        'addLiquidityETH',
        [
          from.token.contractAddress,
          numberToBigNumberFixed(+from.amount, from.token.decimals),
          0,
          0,
          address,
          add(new Date(), { minutes: settings.deadline }).getTime()
        ],
        {
          value: numberToBigNumberFixed(+to.amount, to.token.decimals)
        }
      );
    } else {
      return contract.call('addLiquidity', [
        from.token.contractAddress,
        to.token.contractAddress,
        numberToBigNumberFixed(+from.amount, from.token.decimals),
        numberToBigNumberFixed(+to.amount, to.token.decimals),
        0,
        0,
        address,
        add(new Date(), { minutes: settings.deadline }).getTime()
      ]);
    }
  }, [contract, from.token, to.token, from.amount, to.amount, address]);

  return {
    addLiquidity,
    loading,
    error
  };
}
